/**
 * What each block type lets you change.
 *
 * One switch, one case per block, no clever generic schema. A schema-driven
 * panel would be shorter and would immediately stop being able to say the
 * things that matter here - that a column count is capped because columns do
 * not stack on a phone, that an image width is a percentage because fixed
 * pixels overflow, that a raw HTML block is not checked for you.
 */
import { Image as ImageIcon } from "lucide-react";
import { Button } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { RichTextEditor } from "../../components/RichTextEditor";
import { columnsOf } from "../reducer";
import { useBuilder } from "../store";
import { resolveProps, type Align, type Block, type PageStyle } from "../types";
import {
  AlignField,
  AreaField,
  ChoiceField,
  ColorField,
  Group,
  NumberField,
  Row,
  SpacingField,
  SwitchField,
  TextField,
} from "./controls";
import { SignaturePicker } from "./SignaturePicker";

/** `onPickImage` is threaded down rather than opened here: the media library
 *  needs a site id, which only the screen hosting the builder knows. */
export function BlockFields({
  block,
  page,
  onPickImage,
}: {
  block: Block;
  page: PageStyle;
  onPickImage?: (apply: (url: string) => void) => void;
}) {
  const t = useT();
  const { dispatch } = useBuilder();
  const p = resolveProps(block);

  const set = (patch: Record<string, unknown>) => dispatch({ kind: "update", id: block.id, patch });
  const spacing = (
    <SpacingField top={p.paddingTop} bottom={p.paddingBottom} onChange={(patch) => set(patch)} />
  );

  switch (block.type) {
    case "heading":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <TextField label={t("marketing.builder.prop.text")} value={p.text} onChange={(v) => set({ text: v })} />
            <ChoiceField
              label={t("marketing.builder.prop.level")}
              value={p.level}
              onChange={(v) => set({ level: v })}
              options={[
                { value: "h1", label: t("marketing.builder.level.h1") },
                { value: "h2", label: t("marketing.builder.level.h2") },
                { value: "h3", label: t("marketing.builder.level.h3") },
              ]}
            />
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <AlignField value={p.align} onChange={(v: Align) => set({ align: v })} />
            <ColorField
              label={t("marketing.builder.prop.color")}
              value={p.color}
              inherited={page.textColor}
              onChange={(v) => set({ color: v })}
            />
            <NumberField
              label={t("marketing.builder.prop.fontSize")}
              hint={t("marketing.builder.prop.fontSizeAuto")}
              value={p.fontSize}
              max={72}
              suffix="px"
              onChange={(v) => set({ fontSize: v })}
            />
            {spacing}
          </Group>
        </>
      );

    case "text":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <Row label={t("marketing.builder.prop.body")}>
              <RichTextEditor value={String(p.html ?? "")} onChange={(v) => set({ html: v })} />
            </Row>
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <AlignField value={p.align} onChange={(v: Align) => set({ align: v })} />
            <ColorField
              label={t("marketing.builder.prop.color")}
              value={p.color}
              inherited={page.textColor}
              onChange={(v) => set({ color: v })}
            />
            <div className="grid grid-cols-2 gap-2">
              <NumberField
                label={t("marketing.builder.prop.fontSize")}
                hint={t("marketing.builder.prop.fontSizeAuto")}
                value={p.fontSize}
                max={48}
                suffix="px"
                onChange={(v) => set({ fontSize: v })}
              />
              <Row label={t("marketing.builder.prop.lineHeight")}>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  max="3"
                  value={Number(p.lineHeight ?? 1.6)}
                  onChange={(e) => set({ lineHeight: Number(e.target.value) || 1.6 })}
                  className="h-8 w-full rounded-md border border-input bg-transparent px-2.5 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </Row>
            </div>
            {spacing}
          </Group>
        </>
      );

    case "list":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <AreaField
              label={t("marketing.builder.prop.items")}
              hint={t("marketing.builder.prop.itemsHint")}
              value={p.items}
              rows={6}
              onChange={(v) => set({ items: v })}
            />
            <TextField label={t("marketing.builder.prop.marker")} value={p.marker} onChange={(v) => set({ marker: v })} />
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <ColorField
              label={t("marketing.builder.prop.color")}
              value={p.color}
              inherited={page.textColor}
              onChange={(v) => set({ color: v })}
            />
            {spacing}
          </Group>
        </>
      );

    case "button":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <TextField label={t("marketing.builder.prop.label")} value={p.text} onChange={(v) => set({ text: v })} />
            <TextField
              label={t("marketing.builder.prop.url")}
              hint={t("marketing.builder.prop.urlHint")}
              value={p.url}
              placeholder="https://"
              onChange={(v) => set({ url: v })}
            />
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <AlignField value={p.align} onChange={(v: Align) => set({ align: v })} />
            <div className="grid grid-cols-2 gap-2">
              <ColorField
                label={t("marketing.builder.prop.background")}
                value={p.backgroundColor}
                clearable={false}
                onChange={(v) => set({ backgroundColor: v })}
              />
              <ColorField
                label={t("marketing.builder.prop.textColor")}
                value={p.textColor}
                clearable={false}
                onChange={(v) => set({ textColor: v })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <NumberField
                label={t("marketing.builder.prop.radius")}
                value={p.radius}
                max={40}
                suffix="px"
                onChange={(v) => set({ radius: v })}
              />
              <NumberField
                label={t("marketing.builder.prop.innerX")}
                value={p.innerX}
                max={80}
                suffix="px"
                onChange={(v) => set({ innerX: v })}
              />
            </div>
            <SwitchField
              label={t("marketing.builder.prop.fullWidth")}
              hint={t("marketing.builder.prop.fullWidthHint")}
              value={p.fullWidth}
              onChange={(v) => set({ fullWidth: v })}
            />
            {spacing}
          </Group>
        </>
      );

    case "image":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <TextField
              label={t("marketing.builder.prop.imageUrl")}
              value={p.src}
              placeholder="https://"
              onChange={(v) => set({ src: v })}
            />
            {onPickImage && (
              <Button variant="outline" size="sm" className="w-full" onClick={() => onPickImage((url) => set({ src: url }))}>
                <ImageIcon className="size-3.5" /> {t("marketing.builder.prop.pickImage")}
              </Button>
            )}
            {String(p.src ?? "").startsWith("/") && (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-[10px] leading-snug text-amber-700 dark:text-amber-400">
                {t("marketing.builder.prop.relativeSrc")}
              </p>
            )}
            <TextField
              label={t("marketing.builder.prop.alt")}
              hint={t("marketing.builder.prop.altHint")}
              value={p.alt}
              onChange={(v) => set({ alt: v })}
            />
            <TextField
              label={t("marketing.builder.prop.linkTo")}
              value={p.href}
              placeholder="https://"
              onChange={(v) => set({ href: v })}
            />
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <AlignField value={p.align} onChange={(v: Align) => set({ align: v })} />
            <NumberField
              label={t("marketing.builder.prop.width")}
              hint={t("marketing.builder.prop.widthHint")}
              value={p.width}
              min={10}
              max={100}
              suffix="%"
              onChange={(v) => set({ width: v })}
            />
            <NumberField
              label={t("marketing.builder.prop.radius")}
              value={p.radius}
              max={40}
              suffix="px"
              onChange={(v) => set({ radius: v })}
            />
            {spacing}
          </Group>
        </>
      );

    case "quote":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <AreaField label={t("marketing.builder.prop.text")} value={p.text} onChange={(v) => set({ text: v })} />
            <TextField label={t("marketing.builder.prop.author")} value={p.author} onChange={(v) => set({ author: v })} />
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <div className="grid grid-cols-2 gap-2">
              <ColorField
                label={t("marketing.builder.prop.borderColor")}
                value={p.borderColor}
                clearable={false}
                onChange={(v) => set({ borderColor: v })}
              />
              <ColorField
                label={t("marketing.builder.prop.background")}
                value={p.backgroundColor}
                clearable={false}
                onChange={(v) => set({ backgroundColor: v })}
              />
            </div>
            {spacing}
          </Group>
        </>
      );

    case "columns": {
      const cols = columnsOf(block);
      return (
        <>
          <Group title={t("marketing.builder.group.layout")}>
            <NumberField
              label={t("marketing.builder.prop.columnCount")}
              hint={t("marketing.builder.prop.columnCountHint")}
              value={cols.length}
              min={1}
              max={4}
              onChange={(v) => dispatch({ kind: "columnCount", id: block.id, count: v })}
            />
            <NumberField
              label={t("marketing.builder.prop.gap")}
              value={p.gap}
              max={80}
              suffix="px"
              onChange={(v) => set({ gap: v })}
            />
            {spacing}
          </Group>
        </>
      );
    }

    case "divider":
      return (
        <Group title={t("marketing.builder.group.style")}>
          <ColorField
            label={t("marketing.builder.prop.color")}
            value={p.color}
            clearable={false}
            onChange={(v) => set({ color: v })}
          />
          <NumberField
            label={t("marketing.builder.prop.thickness")}
            value={p.thickness}
            min={1}
            max={12}
            suffix="px"
            onChange={(v) => set({ thickness: v })}
          />
          {spacing}
        </Group>
      );

    case "spacer":
      return (
        <Group title={t("marketing.builder.group.style")}>
          <NumberField
            label={t("marketing.builder.prop.height")}
            value={p.height}
            min={4}
            max={200}
            suffix="px"
            onChange={(v) => set({ height: v })}
          />
        </Group>
      );

    case "signature":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <SignaturePicker props={p} onApply={set} />
            <TextField label={t("marketing.builder.prop.name")} value={p.name} onChange={(v) => set({ name: v })} />
            <TextField label={t("marketing.builder.prop.jobTitle")} value={p.title} onChange={(v) => set({ title: v })} />
            <TextField label={t("marketing.builder.prop.email")} value={p.email} onChange={(v) => set({ email: v })} />
            <TextField label={t("marketing.builder.prop.phone")} value={p.phone} onChange={(v) => set({ phone: v })} />
            <TextField
              label={t("marketing.builder.prop.avatar")}
              hint={t("marketing.builder.prop.avatarHint")}
              value={p.avatar}
              placeholder="https://"
              onChange={(v) => set({ avatar: v })}
            />
            {onPickImage && (
              <Button variant="outline" size="sm" className="w-full" onClick={() => onPickImage((url) => set({ avatar: url }))}>
                <ImageIcon className="size-3.5" /> {t("marketing.builder.prop.pickImage")}
              </Button>
            )}
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <AlignField value={p.align} onChange={(v: Align) => set({ align: v })} />
            {spacing}
          </Group>
        </>
      );

    case "social": {
      const links = (p.links as { id: string; label: string; url: string }[] | undefined) ?? [];
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <p className="text-[10px] leading-snug text-muted-foreground/70">
              {t("marketing.builder.prop.socialHint")}
            </p>
            {links.map((link, i) => (
              <div key={link.id} className="grid grid-cols-2 gap-1.5">
                <TextField
                  label={t("marketing.builder.prop.label")}
                  value={link.label}
                  onChange={(v) =>
                    set({ links: links.map((l, j) => (i === j ? { ...l, label: v } : l)) })
                  }
                />
                <TextField
                  label={t("marketing.builder.prop.url")}
                  value={link.url}
                  onChange={(v) => set({ links: links.map((l, j) => (i === j ? { ...l, url: v } : l)) })}
                />
              </div>
            ))}
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <AlignField value={p.align} onChange={(v: Align) => set({ align: v })} />
            <ColorField
              label={t("marketing.builder.prop.color")}
              value={p.color}
              inherited={page.textColor}
              onChange={(v) => set({ color: v })}
            />
            {spacing}
          </Group>
        </>
      );
    }

    case "footer":
      return (
        <>
          <Group title={t("marketing.builder.group.content")}>
            <AreaField
              label={t("marketing.builder.prop.text")}
              hint={t("marketing.builder.prop.footerHint")}
              value={p.text}
              onChange={(v) => set({ text: v })}
            />
            <TextField
              label={t("marketing.builder.prop.unsubscribeText")}
              hint={t("marketing.builder.prop.unsubscribeHint")}
              value={p.unsubscribeText}
              onChange={(v) => set({ unsubscribeText: v })}
            />
            <TextField
              label={t("marketing.builder.prop.senderAddress")}
              hint={t("marketing.builder.prop.senderAddressHint")}
              value={p.address}
              onChange={(v) => set({ address: v })}
            />
          </Group>
          <Group title={t("marketing.builder.group.style")}>
            <AlignField value={p.align} onChange={(v: Align) => set({ align: v })} />
            <ColorField
              label={t("marketing.builder.prop.color")}
              value={p.color}
              inherited="#98a2b3"
              onChange={(v) => set({ color: v })}
            />
            {spacing}
          </Group>
        </>
      );

    case "html":
      return (
        <Group title={t("marketing.builder.group.content")}>
          <AreaField
            label={t("marketing.builder.prop.html")}
            hint={t("marketing.builder.prop.htmlHint")}
            value={p.html}
            rows={10}
            mono
            onChange={(v) => set({ html: v })}
          />
          {spacing}
        </Group>
      );

    default:
      return null;
  }
}
